import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SaveIcon from "@mui/icons-material/Save";
import React, { useEffect, useMemo, useState } from "react";
import Topbar from "../../../../../components/topbar/TopBar";
import { createClientCityRangeUseCase } from "../domain/usecases/createClientCityRangeUseCase";
import { deleteClientCityRangeUseCase } from "../domain/usecases/deleteClientCityRangeUseCase";
import { getClientCityRangeListUseCase } from "../domain/usecases/getClientCityRangeListUseCase";
import { updateClientCityRangeUseCase } from "../domain/usecases/updateClientCityRangeUseCase";

function createEmptyForm() {
  return {
    min_km: "",
    max_km: "",
    price_km: "",
  };
}

function normalizeValue(value) {
  return value === null || value === undefined ? "" : String(value);
}

function createFormFromRecord(record) {
  if (!record) {
    return createEmptyForm();
  }

  return {
    min_km: normalizeValue(record.min_km),
    max_km: normalizeValue(record.max_km),
    price_km: normalizeValue(record.price_km),
  };
}

function createPayload(form) {
  return {
    min_km: Number(form.min_km),
    max_km: Number(form.max_km),
    price_km: Number(form.price_km),
  };
}

function isFormReady(form) {
  return (
    form.min_km !== "" &&
    !Number.isNaN(Number(form.min_km)) &&
    Number.isInteger(Number(form.min_km)) &&
    Number(form.min_km) >= 0 &&
    form.max_km !== "" &&
    !Number.isNaN(Number(form.max_km)) &&
    Number.isInteger(Number(form.max_km)) &&
    Number(form.max_km) >= 0 &&
    Number(form.max_km) >= Number(form.min_km) &&
    form.price_km !== "" &&
    !Number.isNaN(Number(form.price_km)) &&
    Number(form.price_km) >= 0
  );
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return response?.success === true || message.includes("success");
}

function formatNumber(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return Number(value).toString();
}

function formatPrice(value) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  return Number(value).toFixed(2);
}

function TarifaClientCityRangeForm({
  form,
  onChange,
  onSubmit,
  submitLabel,
  loadingLabel,
  disabled = false,
}) {
  const canSubmit = useMemo(() => isFormReady(form), [form]);

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
        <TextField
          fullWidth
          type="number"
          label="Kilometro minimo"
          value={form.min_km}
          onChange={(event) =>
            onChange({ ...form, min_km: event.target.value })
          }
          inputProps={{ min: "0", step: "1" }}
        />
        <TextField
          fullWidth
          type="number"
          label="Kilometro maximo"
          value={form.max_km}
          onChange={(event) =>
            onChange({ ...form, max_km: event.target.value })
          }
          inputProps={{ min: "0", step: "1" }}
        />
      </Stack>
      <TextField
        fullWidth
        type="number"
        label="Precio por kilometro"
        value={form.price_km}
        onChange={(event) =>
          onChange({ ...form, price_km: event.target.value })
        }
        inputProps={{ min: "0", step: "0.01" }}
      />
      <Button
        fullWidth
        variant="contained"
        startIcon={<SaveIcon />}
        onClick={onSubmit}
        disabled={!canSubmit || disabled}
        sx={{
          mt: 1,
          minHeight: 50,
          borderRadius: 2,
          textTransform: "uppercase",
          fontWeight: 700,
          bgcolor: "#165c53",
          color: "#fff",
          "&:hover": {
            bgcolor: "#12483f",
          },
        }}
      >
        {(disabled ? loadingLabel : submitLabel).toUpperCase()}
      </Button>
    </Stack>
  );
}

export const TarifaClientCityRangePage = () => {
  const [form, setForm] = useState(createEmptyForm());
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(createEmptyForm());
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const loadRatesCityRange = async ({ silent = false } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      const rates = await getClientCityRangeListUseCase();
      setRecords(rates);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo cargar la lista de tarifas.",
      });
      setRecords([]);
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadRatesCityRange();
  }, []);

  const handleCreate = async () => {
    const payload = createPayload(form);

    try {
      setSaving(true);
      const response = await createClientCityRangeUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo crear la tarifa por rango.");
      }

      setForm(createEmptyForm());
      setMessage({
        open: true,
        type: "success",
        text: "Tarifa por rango creada correctamente.",
      });
      await loadRatesCityRange({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo crear la tarifa por rango.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenMenu = (event, record) => {
    setMenuAnchor(event.currentTarget);
    setSelectedRecord(record);
  };

  const handleCloseMenu = () => {
    setMenuAnchor(null);
  };

  const handleOpenEdit = () => {
    setEditForm(createFormFromRecord(selectedRecord));
    setEditOpen(true);
    handleCloseMenu();
  };

  const handleCloseEdit = () => {
    setEditOpen(false);
    setSelectedRecord(null);
    setEditForm(createEmptyForm());
  };

  const handleUpdate = async () => {
    const payload = {
      id: selectedRecord.id,
      ...createPayload(editForm),
    };

    try {
      setSaving(true);
      const response = await updateClientCityRangeUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo actualizar la tarifa por rango.");
      }

      handleCloseEdit();
      setMessage({
        open: true,
        type: "success",
        text: "Tarifa por rango actualizada correctamente.",
      });
      await loadRatesCityRange({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo actualizar la tarifa por rango.",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDelete = () => {
    setDeleteOpen(true);
    handleCloseMenu();
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedRecord(null);
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      const response = await deleteClientCityRangeUseCase(selectedRecord.id);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo eliminar la tarifa por rango.");
      }

      handleCloseDelete();
      setMessage({
        open: true,
        type: "success",
        text: "Tarifa por rango eliminada correctamente.",
      });
      await loadRatesCityRange({ silent: true });
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo eliminar la tarifa por rango.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 960,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight={800}
                  sx={{ color: "#fff !important" }}
                >
                  CREAR TARIFA CLIENTE POR RANGO DE KILOMETROS
                </Typography>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                <Stack spacing={3}>
                  <Paper
                    elevation={0}
                    sx={{
                      mx: "auto",
                      width: "100%",
                      maxWidth: 560,
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      p: { xs: 1, md: 2 },
                    }}
                  >
                    <TarifaClientCityRangeForm
                      form={form}
                      onChange={setForm}
                      onSubmit={handleCreate}
                      submitLabel="Crear"
                      loadingLabel="Creando..."
                      disabled={saving}
                    />
                  </Paper>

                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      overflowX: "auto",
                    }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: "#f4faf7",
                            "& th": {
                              fontWeight: 800,
                              color: "#12483f",
                            },
                          }}
                        >
                          <TableCell>Kilometro minimo</TableCell>
                          <TableCell>Kilometro maximo</TableCell>
                          <TableCell>Precio Km</TableCell>
                          <TableCell align="right">Opciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {loading ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Stack
                                alignItems="center"
                                spacing={2}
                                sx={{ py: 4 }}
                              >
                                <CircularProgress />
                                <Typography color="text.secondary">
                                  Cargando tarifas...
                                </Typography>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ) : records.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography sx={{ py: 3 }} color="text.secondary">
                                No hay tarifas registradas.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          records.map((record) => (
                            <TableRow key={record.id} hover>
                              <TableCell>
                                {formatNumber(record.min_km)}
                              </TableCell>
                              <TableCell>
                                {formatNumber(record.max_km)}
                              </TableCell>
                              <TableCell>
                                {formatPrice(record.price_km)}
                              </TableCell>
                              <TableCell align="right">
                                <IconButton
                                  aria-label="Opciones"
                                  onClick={(event) =>
                                    handleOpenMenu(event, record)
                                  }
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </Box>
            </Paper>
          </Stack>

          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={handleCloseMenu}
          >
            <MenuItem onClick={handleOpenEdit}>Editar</MenuItem>
            <MenuItem onClick={handleOpenDelete}>Eliminar</MenuItem>
          </Menu>

          <Dialog
            fullWidth
            maxWidth="sm"
            open={editOpen}
            onClose={handleCloseEdit}
          >
            <DialogTitle>Editar tarifa por rango</DialogTitle>
            <DialogContent>
              <Box sx={{ pt: 1 }}>
                <TarifaClientCityRangeForm
                  form={editForm}
                  onChange={setEditForm}
                  onSubmit={handleUpdate}
                  submitLabel="actualizar"
                  loadingLabel="actualizando..."
                  disabled={saving}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseEdit}>cancelar</Button>
            </DialogActions>
          </Dialog>

          <Dialog open={deleteOpen} onClose={handleCloseDelete}>
            <DialogTitle>Deseas Eliminar esta Tarifa por rango?</DialogTitle>
            <DialogActions>
              <Button onClick={handleCloseDelete} disabled={deleting}>
                cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "eliminando..." : "aceptar"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};

export default TarifaClientCityRangePage;
